class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;
    this.addEventListener('change', () => this.onChange());
  }

  get variants() {
    if (!this._variants) {
      this._variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    }
    return this._variants;
  }

  onChange() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    let selected;
    if (fieldsets.length) {
      selected = fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        const label = fieldset.querySelector('[data-selected-label]');
        if (checked && label) label.textContent = checked.value;
        return checked ? checked.value : null;
      });
    } else {
      selected = Array.from(this.querySelectorAll('select'), (select) => select.value);
    }
    const variant = this.variants.find((v) => v.options.every((option, i) => option === selected[i]));
    this.updateForm(variant);
    if (variant) {
      this.refreshPrice(variant);
      if (window.MX) window.MX.publish('variant:change', variant);
    }
  }

  updateForm(variant) {
    const root = this.closest('[data-product]') || document;
    const idInputs = root.querySelectorAll('input[name="id"]');
    const submit = root.querySelector('button[name="add"]');
    const label = submit ? submit.querySelector('span') : null;

    if (!variant) {
      if (submit) submit.disabled = true;
      if (label) label.textContent = window.strings.unavailable;
      return;
    }
    idInputs.forEach((input) => {
      input.value = variant.id;
    });
    const sku = root.querySelector('[data-sku]');
    if (sku) sku.textContent = variant.sku || '';
    if (submit) submit.disabled = !variant.available;
    if (label) label.textContent = variant.available ? window.strings.addToCart : window.strings.soldOut;
    window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
  }

  static async fetchDoc(url) {
    const response = await fetch(url);
    if (!response.ok) return null;
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  async refreshPrice(variant) {
    const url = `${this.productUrl}?variant=${variant.id}`;
    let html = null;
    try {
      html = await VariantSelects.fetchDoc(`${url}&section_id=${this.sectionId}`);
      if (!html) html = await VariantSelects.fetchDoc(url);
    } catch (e) {
      html = null;
    }
    if (!html) {
      console.warn('[product-form] price refresh failed for variant', variant.id);
      return;
    }
    const priceId = `Price-${this.sectionId}`;
    const fresh = html.getElementById(priceId);
    const current = document.getElementById(priceId);
    if (fresh && current) current.innerHTML = fresh.innerHTML;
    document.querySelectorAll('[data-variant-refresh][id]').forEach((el) => {
      const freshEl = html.getElementById(el.id);
      if (freshEl) el.innerHTML = freshEl.innerHTML;
    });
    if (window.MX) window.MX.publish('variant:refresh', variant);
  }
}
customElements.define('variant-selects', VariantSelects);

let apoAddonCents = 0;

function applyAddonPrice() {
  const priceEl = document.querySelector('[id^="Price-"] .price');
  const currentEl = priceEl ? priceEl.querySelector('.price__current') : null;
  if (!currentEl) return;
  const base = parseInt(priceEl.dataset.base, 10) || 0;
  if (!base) return;
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  let money;
  try {
    money = new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
  } catch (e) {
    money = new Intl.NumberFormat(undefined, { style: 'currency', currency });
  }
  currentEl.textContent = money.format((base + apoAddonCents) / 100);
}

if (window.MX) {
  window.MX.subscribe('price:addon', (cents) => {
    apoAddonCents = parseInt(cents, 10) || 0;
    applyAddonPrice();
  });
  window.MX.subscribe('variant:refresh', () => {
    if (apoAddonCents > 0) applyAddonPrice();
  });
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.error = this.querySelector('.product-form__error');
    if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
  }

  static async parseJson(response) {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (err) {
      console.error('[product-form] Expected JSON from', response.url, '- got:', text.slice(0, 200));
      throw new Error(window.strings.error);
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    const submit = this.form.querySelector('button[name="add"]');
    const label = submit ? submit.querySelector('span') : null;
    if (submit) {
      submit.setAttribute('aria-busy', 'true');
      submit.disabled = true;
    }
    if (this.error) this.error.hidden = true;

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        body: new FormData(this.form),
      });
      const data = await ProductForm.parseJson(response);
      if (!response.ok) {
        throw new Error(data.description || window.strings.error);
      }

      const cart = await ProductForm.parseJson(await fetch(`${window.routes.cart_url}.js`));
      window.MX.publish('cart:update', cart);
      window.MX.publish('cart:added', { item: data, cart });

      const detail = { item: data, cart };
      ['cart:refresh', 'cart:updated', 'cart:change'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      });

      if (label) {
        const original = label.textContent;
        label.textContent = window.strings.added;
        setTimeout(() => {
          label.textContent = original;
        }, 2000);
      }
    } catch (err) {
      if (this.error) {
        this.error.textContent = err.message;
        this.error.hidden = false;
      }
    } finally {
      if (submit) {
        submit.removeAttribute('aria-busy');
        submit.disabled = false;
      }
    }
  }
}
customElements.define('product-form', ProductForm);

if (window.MX) {
  window.MX.subscribe('variant:change', (variant) => {
    const el = document.querySelector('[data-product-stock]');
    if (!el || !variant) return;
    if (!variant.available) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    const ids = (el.dataset.preorderIds || '').split(',').filter(Boolean);
    const isPreorder = ids.indexOf(String(variant.id)) !== -1;
    el.textContent = isPreorder ? el.dataset.tPreorder : el.dataset.tIn;
    el.classList.toggle('product__stock--preorder', isPreorder);
  });
}
